import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { RefreshCw, ChevronDown, ChevronRight, Truck, Save } from "lucide-react";

const STATUS_COLOR = {
  WORKING: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  READY_TO_SHIP: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
  SHIPPED: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  IN_TRANSIT: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  DELIVERED: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400",
  CHECKED_IN: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400",
  RECEIVING: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-400",
  CLOSED: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  CANCELLED: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
  DELETED: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
};

const FILTERS = [
  { key: "active", label: "Active" },
  { key: "closed", label: "Closed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

const MetaEditor = ({ s, onSaved }) => {
  const [appt, setAppt] = useState(s.appointment_at || "");
  const [vehicle, setVehicle] = useState(s.vehicle || "");
  const [remarks, setRemarks] = useState(s.remarks || "");
  const [saving, setSaving] = useState(false);
  const dirty = appt !== (s.appointment_at || "") || vehicle !== (s.vehicle || "") || remarks !== (s.remarks || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tools/shipments/${s.shipment_id}/meta`, { appointment_at: appt, vehicle, remarks });
      toast.success(`${s.shipment_id} details saved`);
      onSaved({ appointment_at: appt, vehicle, remarks });
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <td className="px-3 py-2">
        <Input type="datetime-local" value={appt} onChange={(e) => setAppt(e.target.value)}
          className="h-8 text-xs w-44" data-testid={`ship-appt-${s.shipment_id}`} />
      </td>
      <td className="px-3 py-2">
        <Input placeholder="Vehicle no. / driver" value={vehicle} onChange={(e) => setVehicle(e.target.value)}
          className="h-8 text-xs w-40" data-testid={`ship-vehicle-${s.shipment_id}`} />
      </td>
      <td className="px-3 py-2">
        <Input placeholder="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)}
          className="h-8 text-xs w-48" data-testid={`ship-remarks-${s.shipment_id}`} />
      </td>
      <td className="px-3 py-2">
        <Button size="sm" variant={dirty ? "default" : "ghost"} className="h-8 rounded-full px-3"
          disabled={!dirty || saving} onClick={save} data-testid={`ship-save-${s.shipment_id}`}>
          <Save className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </td>
    </>
  );
};

export const ShipmentsPipeline = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("active");

  const load = (f = filter) => api.get(`/tools/shipments?status=${f}`, { timeout: 120000 })
    .then(({ data }) => setData(data))
    .catch(() => {
      toast.error("Failed to load shipments");
      setData((d) => d || { shipments: [], count: 0, counts: null, last_sync: null });
    });
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post(`/tools/shipments/refresh?status=${filter}`, {}, { timeout: 120000 });
      setData(d);
      toast.success("Shipment statuses refreshed from Amazon");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const patchRow = (sid, meta) => setData((d) => ({
    ...d, shipments: d.shipments.map((s) => (s.shipment_id === sid ? { ...s, ...meta } : s)),
  }));

  if (!data) return <Skeleton className="h-72" />;

  return (
    <div className="space-y-5" data-testid="shipments-pipeline-tool">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          <Truck className="h-4 w-4 inline mr-1.5 -mt-0.5" aria-hidden="true" />
          <strong>{data.count}</strong> shipments shown (live from Amazon, last synced {data.last_sync ? new Date(data.last_sync).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "never"}).
          Appointment, vehicle & remarks are yours to edit — statuses come from Amazon.
        </p>
        <Button className="rounded-full" onClick={refresh} disabled={refreshing} data-testid="ship-refresh-button">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
          {refreshing ? "Refreshing…" : "Refresh status"}
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="ship-filter-tabs">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            data-testid={`ship-filter-${f.key}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === f.key ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-muted-foreground border-border hover:text-foreground"}`}>
            {f.label}{data.counts ? ` (${data.counts[f.key] ?? 0})` : ""}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="shipments-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {["", "Shipment ID", "Status", "Dest. FC", "SKUs", "Units", "Appointment", "Vehicle", "Remarks", ""].map((h, i) => (
                <th key={i} className="text-left px-3 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.shipments.map((s) => (
              <FragmentRow key={s.shipment_id} s={s} open={!!open[s.shipment_id]}
                toggle={() => setOpen((o) => ({ ...o, [s.shipment_id]: !o[s.shipment_id] }))}
                patchRow={patchRow} />
            ))}
            {data.shipments.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground" data-testid="ship-empty">
                No {filter === "all" ? "" : filter + " "}shipments found. Click "Refresh status" to re-check Amazon.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FragmentRow = ({ s, open, toggle, patchRow }) => (
  <>
    <tr className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`ship-row-${s.shipment_id}`}>
      <td className="pl-3 py-2 w-8">
        <button onClick={toggle} data-testid={`ship-expand-${s.shipment_id}`} className="p-1 text-muted-foreground hover:text-foreground">
          {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        </button>
      </td>
      <td className="px-3 py-2">
        <p className="font-mono text-xs font-semibold">{s.shipment_id}</p>
        <p className="text-[10px] text-muted-foreground max-w-[11rem] truncate">{s.name}</p>
      </td>
      <td className="px-3 py-2"><Badge className={`border-0 ${STATUS_COLOR[s.status] || ""}`}>{s.status.replaceAll("_", " ")}</Badge></td>
      <td className="px-3 py-2 font-mono text-xs">{s.destination_fc}</td>
      <td className="px-3 py-2">{s.items?.length || 0}</td>
      <td className="px-3 py-2">{(s.total_units || 0).toLocaleString("en-IN")}</td>
      <MetaEditor s={s} onSaved={(meta) => patchRow(s.shipment_id, meta)} />
    </tr>
    {open && (
      <tr className="bg-muted/30" data-testid={`ship-items-${s.shipment_id}`}>
        <td colSpan={10} className="px-6 py-3">
          {(s.items || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No item data synced for this shipment yet — hit "Refresh status".</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-1.5">
              {s.items.map((i) => (
                <div key={i.sku} className="flex items-center gap-2.5 text-xs py-1 border-b border-border/50">
                  <SkuThumb sku={i.sku} alt={i.sku} size="h-8 w-8" />
                  <span className="font-mono flex-1 truncate">{i.sku}</span>
                  <span>shipped <strong>{i.qty_shipped}</strong></span>
                  <span className={i.qty_received < i.qty_shipped ? "text-amber-600" : "text-green-600"}>
                    received <strong>{i.qty_received}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}
        </td>
      </tr>
    )}
  </>
);
